import { Direction, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { EntityType, ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread as UnifiedThread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { dedupeInboxTagsArray } from '../../../utils';
import { isSmsMessageItem } from '../../../utils/thread-items-utils';

type MatchUnifiedThreadToSmsArgs = {
  thread: UnifiedThread;
  originalSms?: SMS;
  updatedSms?: SMS;
};

const getKnownThreadStatusFromSms = (status: Status, originalStatus: ThreadReadStatus): ThreadReadStatus => {
  if (status === Status.STATUS_ERROR) return ThreadReadStatus.THREAD_READ_STATUS_ERROR;
  if (status === Status.STATUS_NEW) return ThreadReadStatus.THREAD_READ_STATUS_NEW;
  if (status === Status.STATUS_READ) return ThreadReadStatus.THREAD_READ_STATUS_READ;
  return originalStatus;
};

export const matchUnifiedThreadToSms = ({
  thread,
  originalSms,
  updatedSms,
}: MatchUnifiedThreadToSmsArgs): UnifiedThread => {
  // Key comparisons using originalSms vs updatedSms (mimicking original logic)
  const isDifferentSms = originalSms?.id !== updatedSms?.id;
  const smsStatusHasChanged = originalSms?.status !== updatedSms?.status;
  const hasNewErrorStatus = smsStatusHasChanged && updatedSms?.status === Status.STATUS_ERROR;
  const hasNewInboundMessage = isDifferentSms && updatedSms?.direction === Direction.DIRECTION_INBOUND;
  const hasNewOutboundMessage = isDifferentSms && updatedSms?.direction === Direction.DIRECTION_OUTBOUND;

  // Update the SMS message within the ThreadItem structure
  const updatedItems =
    thread.items?.map((item) => {
      if (isSmsMessageItem(item) && item.smsMessage?.id === updatedSms?.id) {
        return {
          ...item,
          smsMessage: updatedSms,
        };
      }
      return item;
    }) || [];

  // If we have a new SMS (different SMS), we need to add it to the items array
  // For unified threads, new SMS should be added as the first item (newest first)
  let finalItems = updatedItems;
  if (isDifferentSms && updatedSms) {
    // Remove any existing item with the same SMS id first
    const filteredItems = updatedItems.filter(
      (item) => !isSmsMessageItem(item) || item.smsMessage?.id !== updatedSms.id
    );

    // Add the new SMS as the first item
    finalItems = [{ smsMessage: updatedSms }, ...filteredItems];
  }

  return {
    ...thread,
    items: finalItems,

    status:
      hasNewOutboundMessage && !updatedSms?.autogeneratedBy
        ? ThreadReadStatus.THREAD_READ_STATUS_READ
        : smsStatusHasChanged && updatedSms?.status
        ? getKnownThreadStatusFromSms(updatedSms.status, thread.status)
        : thread.status,

    actionable: hasNewErrorStatus || hasNewInboundMessage ? true : thread.actionable,

    // Tag merging for unified threads - only merge tags when we have a different SMS
    // This prevents tag duplication during normal sync operations
    uniqueTags: isDifferentSms
      ? dedupeInboxTagsArray([
          ...(thread.uniqueTags ?? []),
          ...(updatedSms?.tagsDetailed?.map((smsTag) => ({
            // Convert SMSTag to InboxTag format for unified threads
            tagId: smsTag.tagId,
            entityId: smsTag.smsId,
            entityType: EntityType.ENTITY_TYPE_SMS,
            entityCreatedAt: smsTag.smsCreatedAt,
            appliedBy: smsTag.appliedBy,
          })) ?? []),
        ])
      : thread.uniqueTags, // Keep existing tags for same SMS updates

    // Reply status logic (exactly mirroring original)
    isReplied:
      isDifferentSms && updatedSms?.direction === Direction.DIRECTION_OUTBOUND && !updatedSms?.autogeneratedBy
        ? true
        : thread.isReplied,

    // Archive logic (exactly mirroring original)
    isArchived: isDifferentSms && !updatedSms?.autogeneratedBy ? false : thread.isArchived,
  };
};
